import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Button, Modal, Form, Alert } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faQuestionCircle, faInfoCircle, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/api';
import Ranked from '../components/Ciem/Ranked';
import CardLayout from '../components/Layout/CardLayout';

const GastosPage = () => {
    const [investments, setInvestments] = useState([]);
    const [totalGastoIncurrido, setTotalGastoIncurrido] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showAyudaModal, setShowAyudaModal] = useState(false);
    const [showRecuerdeModal, setShowRecuerdeModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [selectedInvestment, setSelectedInvestment] = useState(null);
    const [formData, setFormData] = useState({
        rubro: '',
        gastoIncurrido: '',
        justificacion: ''
    });

    const ciemId = useSelector(state => state.ciem.ciemId);
    const [rankedForm, setRankedForm] = useState([]);

    // Fetch investments on component mount
    useEffect(() => {
        if (ciemId) {
            fetchInvestments();
        } else {
            setLoading(false);
        }

        axiosInstance.get('/detailoption/option/value/rankedSixteen').then((response) => {
            console.log("Calificación obtenida:", response.data.data[0]);
            setRankedForm(response.data.data[0]);
        }).catch((e) => {
            alert("Error consultando la calificacion " + e)
        });
    }, [ciemId]);

    const fetchInvestments = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/nonRecoverableInvestment/${ciemId}`);
            // Handle nested data structure - response.data.data
            const data = response.data.data || response.data;
            setInvestments(data.investments || []);
            setTotalGastoIncurrido(data.totalGastoIncurrido || 0);
            setError(null);
        } catch (err) {
            setError('Error al cargar las inversiones no recuperables: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleShowCreateModal = () => {
        setModalMode('create');
        setSelectedInvestment(null);
        setFormData({
            rubro: '',
            gastoIncurrido: '',
            justificacion: ''
        });
        setShowModal(true);
    };

    const handleShowEditModal = (investment) => {
        setModalMode('edit');
        setSelectedInvestment(investment);
        setFormData({
            rubro: investment.rubro || '',
            gastoIncurrido: investment.gastoIncurrido || '',
            justificacion: investment.justificacion || ''
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedInvestment(null);
        setFormData({
            rubro: '',
            gastoIncurrido: '',
            justificacion: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                id: selectedInvestment ? selectedInvestment.id : null,
                rubro: formData.rubro,
                gastoIncurrido: formData.gastoIncurrido ? parseFloat(formData.gastoIncurrido) : null,
                justificacion: formData.justificacion,
                ciemId: ciemId
            };

            if (modalMode === 'create') {
                // POST expects an array of investments with ciemId in URL
                await axiosInstance.post(`/nonRecoverableInvestment/${ciemId}`, [submitData]);
            } else {
                // Use PUT method with id in the URL path
                await axiosInstance.put(`/nonRecoverableInvestment/${selectedInvestment.id}`, submitData);
            }
            handleCloseModal();
            fetchInvestments();
        } catch (err) {
            setError(`Error al ${modalMode === 'create' ? 'crear' : 'actualizar'} inversión no recuperable: ` + err.message);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de que desea eliminar esta inversión no recuperable?')) {
            try {
                await axiosInstance.delete(`/nonRecoverableInvestment/${id}`);
                fetchInvestments();
            } catch (err) {
                setError('Error al eliminar inversión no recuperable: ' + err.message);
            }
        }
    };

    if (!ciemId) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    Por favor, seleccione un CIEM para ver las inversiones no recuperables.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            <Row>
                <Col xs={12} className="p-3">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center">
                                    <h3 className="mb-0 me-3">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                                        Gastos
                                    </h3>
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => setShowAyudaModal(true)}
                                        title="Ayuda"
                                    >
                                        <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                                        Ayuda
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => setShowRecuerdeModal(true)}
                                        title="Recuerde"
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                        Recuerde
                                    </Button>
                                </div>
                                <Button variant="primary" onClick={handleShowCreateModal}>
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Crear
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>Rubro</th>
                                                <th>Gasto Incurrido</th>
                                                <th>Justificación</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {investments.length === 0 ? (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-4">
                                                        No hay inversiones no recuperables registradas
                                                    </td>
                                                </tr>
                                            ) : (
                                                investments.map((investment) => (
                                                    <tr key={investment.id}>
                                                        <td>{investment.rubro || '-'}</td>
                                                        <td>{investment.gastoIncurrido !== null && investment.gastoIncurrido !== undefined ? investment.gastoIncurrido : '-'}</td>
                                                        <td>{investment.justificacion || '-'}</td>
                                                        <td>
                                                            <Button
                                                                variant="info"
                                                                size="sm"
                                                                className="me-2"
                                                                onClick={() => handleShowEditModal(investment)}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() => handleDelete(investment.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        {investments.length > 0 && (
                                            <tfoot>
                                                <tr className="fw-bold">
                                                    <td>Total</td>
                                                    <td>{totalGastoIncurrido}</td>
                                                    <td colSpan="2"></td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </Table>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Crear Nueva Inversión No Recuperable' : 'Editar Inversión No Recuperable'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Rubro</Form.Label>
                            <Form.Control
                                type="text"
                                name="rubro"
                                value={formData.rubro}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Gasto Incurrido</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                name="gastoIncurrido"
                                value={formData.gastoIncurrido}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Justificación</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="justificacion"
                                value={formData.justificacion}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            {modalMode === 'create' ? 'Crear' : 'Actualizar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de Ayuda */}
            <Modal show={showAyudaModal} onHide={() => setShowAyudaModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Ayuda</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ textAlign: 'justify' }}>
                        Considere este punto, solo en caso de observarse la necesidad de hacer un estudio de factibilidad de la idea de negocio más profundo que el ya realizado con el sistema Mi Modelo de Negocio, lo que es válido en proyectos de gran envergadura, por alta inversión y amplio mercado a servir, o en caso de innovaciones o inventos que requieran prototipos de los productos.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAyudaModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Recuerde */}
            <Modal show={showRecuerdeModal} onHide={() => setShowRecuerdeModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Recuerde</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ textAlign: 'justify' }}>
                        <p>
                            Si se requiere un estudio de factibilidad o plan de negocio más amplio y profundo, esta tarea demanda esfuerzo personal, tiempo y otros recursos que se reflejan en costos o gastos monetarios que deben ser cubiertos por los emprendedores e inversores comprometidos con la iniciativa.
                        </p>
                        <p>
                            Estas salidas de dinero son anteriores a la decisión final sobre invertir o no en el proyecto, también se dan después de tomada la decisión positiva, pero en todo caso estas erogaciones no se pueden contabilizar entre los activos tangibles o intangibles que quedarán a favor de la empresa una vez puesta en marcha. Dichas erogaciones son las que se presentan para poder medir y evaluar la posibilidad cierta de la oportunidad de negocio y se derivan del proceso de estudio, por lo que los inversores comprometidos en el propósito emprendedor deben asumirlos como un gasto personal, sea que se materialice o no el proyecto emprendedor.
                        </p>
                        <p>
                            Así pues, los gastos relacionados en el formato siguiente, tienen solo el objetivo de mostrarse ante los inversores, para que estos tengan claro su existencia, y conozcan que deben cubrirlos, aunque los mismos no quedarán incluidos en el monto de la inversión total que se contabilizará en los libros de la empresa una vez inicie operaciones, por lo cual son gastos no reembolsables, sea que se monte o no la empresa.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRecuerdeModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Ranked - Calificación */}
            {(() => {
                const calificacion = <Ranked field={rankedForm} ciemId={ciemId} />;
                return <CardLayout value={"rankedSixteen"} tablaComponent={calificacion} />;
            })()}
        </Container>
    );
};

export default GastosPage;
